/**
 * Free Shipping Progress - Auto-add/remove free gift based on threshold
 * When a tier with reward type "free gift" is qualified, adds that product to the cart.
 * When threshold is no longer met, removes the free gift from the cart.
 *
 * Reliability + speed:
 * - The gift config (variant ids + thresholds) is cached once seen, so cart re-renders
 *   never make the script lose track of it.
 * - If the cart panel isn't rendered yet (right after a product-page add, or from an empty
 *   cart), we wait and retry instead of giving up, so the gift lands on the first cross.
 * - The current total is always confirmed live (event payload when available, else a single
 *   GET), so we never act on a stale snapshot.
 */
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

let isModifyingGift = false;
let syncQueued = false;
let pendingCart = null;
let cachedGiftConfig = null;

function getCartJsUrl() {
  const root = window.Shopify?.routes?.root;
  if (typeof root === 'string' && root.length > 0) {
    return `${root.endsWith('/') ? root : `${root}/`}cart.js`;
  }
  const base = window.Theme?.routes?.cart_url;
  if (typeof base === 'string' && base.length > 0) {
    return base.replace(/\/?$/, '') + '.js';
  }
  return '/cart.js';
}

function getCartChangeJsUrl() {
  const raw = window.Theme?.routes?.cart_change_url ?? '/cart/change.js';
  const url = String(raw).trim();
  return url.includes('.js') ? url : url.replace(/\/?$/, '') + '.js';
}

function getCartAddJsUrl() {
  const raw = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
  const url = String(raw).trim();
  return url.includes('.js') ? url : url.replace(/\/?$/, '') + '.js';
}

async function readCartApiJson(response, label) {
  const text = await response.text();
  const trimmed = text.trimStart();
  if (trimmed.startsWith('<')) {
    console.warn(
      `[free-shipping-progress] ${label}: expected JSON, got HTML (status ${response.status}).`,
      trimmed.slice(0, 160)
    );
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    console.warn(`[free-shipping-progress] ${label}: JSON parse failed`, e);
    return null;
  }
}

/**
 * Pull a usable cart object out of a cart:update event so we can skip GET /cart.js.
 * cart-items-component dispatches the full /cart/change.js response as `resource`
 * (includes total_price + items). Adds from the product page do not, so we return null.
 */
function getCartFromEvent(event) {
  const cart = event?.detail?.resource;
  if (cart && typeof cart.total_price === 'number' && Array.isArray(cart.items)) {
    return cart;
  }
  return null;
}

/**
 * Read the gift config (variant ids + thresholds in cents) from the progress bar element.
 * Returns null if the progress bar isn't in the DOM yet.
 */
function readGiftConfig() {
  const progressEl = document.querySelector('.free-shipping-progress[data-gift-variant-ids]');
  if (!progressEl) return null;
  const idsStr = progressEl.dataset.giftVariantIds;
  const thrStr = progressEl.dataset.giftThresholds;
  if (!idsStr || !thrStr) return null;
  const variantIds = idsStr.split(',').map((s) => s.trim()).filter(Boolean);
  const thresholds = thrStr.split(',').map((t) => parseInt(t.trim(), 10) || 0);
  if (variantIds.length === 0 || variantIds.length !== thresholds.length) return null;
  return {
    config: variantIds.map((vid, i) => ({ variantId: vid, thresholdCents: thresholds[i] || 0 })),
    variantIds,
  };
}

function getSectionId() {
  const progressEl = document.querySelector('.free-shipping-progress[data-gift-variant-ids]');
  const fromProgress = progressEl?.closest('cart-items-component')?.dataset?.sectionId;
  if (fromProgress) return fromProgress;
  const anyComp = document.querySelector('cart-items-component');
  return anyComp?.dataset?.sectionId || null;
}

async function syncFreeGifts(providedCart, attempt = 0) {
  // Cache config the first time the progress bar is in the DOM; reuse it afterwards.
  const domConfig = readGiftConfig();
  if (domConfig) cachedGiftConfig = domConfig;
  const giftData = cachedGiftConfig;
  const sectionId = getSectionId();

  // The cart panel can be a render behind right after a product-page add. Wait and retry
  // instead of bailing, so the gift still lands on the first threshold cross.
  if (!giftData || !sectionId) {
    if (attempt < 12) {
      setTimeout(() => syncFreeGifts(providedCart, attempt + 1), 120);
    }
    return;
  }

  if (isModifyingGift) return;

  const { config: giftConfig, variantIds } = giftData;
  const giftIdSet = new Set(variantIds);

  isModifyingGift = true;
  try {
    // Always confirm the authoritative total. Use the event payload when it carries the
    // full cart; otherwise a single GET (the DOM can lag on product-page adds).
    let currentTotal = null;
    const giftInCartMap = new Map();

    if (providedCart && typeof providedCart.total_price === 'number' && Array.isArray(providedCart.items)) {
      currentTotal = providedCart.total_price;
      providedCart.items.forEach((item, idx) => {
        const vid = String(item.variant_id);
        if (giftIdSet.has(vid)) giftInCartMap.set(vid, idx + 1);
      });
    } else {
      const cartRes = await fetch(getCartJsUrl(), {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      const cart = await readCartApiJson(cartRes, 'GET cart.js');
      if (!cart || typeof cart !== 'object') return;
      currentTotal = cart.total_price || 0;
      (cart.items || []).forEach((item, idx) => {
        const vid = String(item.variant_id);
        if (giftIdSet.has(vid)) giftInCartMap.set(vid, idx + 1);
      });
    }

    if (typeof currentTotal !== 'number' || Number.isNaN(currentTotal)) return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = Array.from(cartItemsComponents)
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length === 0) sectionIds.push(sectionId);

    // Remove gifts that no longer qualify
    const toRemove = [];
    for (const cfg of giftConfig) {
      const line = giftInCartMap.get(cfg.variantId);
      if (line && cfg.thresholdCents > 0 && currentTotal < cfg.thresholdCents) {
        toRemove.push({ line, variantId: cfg.variantId });
      }
    }

    if (toRemove.length > 0) {
      toRemove.sort((a, b) => b.line - a.line);
      for (const { line } of toRemove) {
        const body = JSON.stringify({
          line,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        });
        const response = await fetch(getCartChangeJsUrl(), fetchConfig('json', { body }));
        const data = (await readCartApiJson(response, 'POST cart/change.js')) ?? {};
        if (data.errors) {
          console.warn('Free gift remove:', data.errors);
          continue;
        }
        if (data.sections?.[sectionId]) {
          morphSection(sectionId, data.sections[sectionId]);
        } else {
          sectionRenderer.renderSection(sectionId, { cache: false });
        }
        const itemCount = data.item_count ?? 0;
        document.dispatchEvent(
          new CartUpdateEvent(data, sectionId, {
            source: 'free-shipping-progress',
            itemCount,
            sections: data.sections,
          })
        );
      }
      return;
    }

    // Add gifts that now qualify and aren't in the cart yet
    const toAdd = giftConfig.filter(
      (g) => g.thresholdCents > 0 && !giftInCartMap.has(g.variantId) && currentTotal >= g.thresholdCents
    );
    if (toAdd.length === 0) return;

    for (const { variantId: vid } of toAdd) {
      const formData = new FormData();
      formData.append('id', vid);
      formData.append('quantity', '1');
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(getCartAddJsUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = (await readCartApiJson(response, 'POST cart/add.js')) ?? {};

      if (data.status || !response.ok) {
        console.warn('Free gift add:', data.message || 'Failed to add');
        continue;
      }

      const sections = data.sections || {};
      if (sections[sectionId]) {
        morphSection(sectionId, sections[sectionId]);
      } else {
        sectionRenderer.renderSection(sectionId, { cache: false });
      }

      document.dispatchEvent(
        new CartAddEvent(
          {},
          'free-shipping-progress',
          {
            source: 'free-shipping-progress',
            itemCount: 1,
            variantId: String(vid),
            sections,
          }
        )
      );
    }
  } catch (err) {
    console.error('Free shipping progress gift sync error:', err);
  } finally {
    isModifyingGift = false;
  }
}

function scheduleSync(cart) {
  if (cart) pendingCart = cart;
  if (syncQueued) return;
  syncQueued = true;
  requestAnimationFrame(() => {
    syncQueued = false;
    const c = pendingCart;
    pendingCart = null;
    syncFreeGifts(c, 0);
  });
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === 'free-shipping-progress') return;
  scheduleSync(getCartFromEvent(event));
});

document.addEventListener(DialogOpenEvent.eventName, () => scheduleSync());

customElements.whenDefined('cart-items-component').then(() => {
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.addedNodes.length) {
        scheduleSync();
        break;
      }
    }
  });

  const cartDrawer = document.querySelector('cart-drawer-component');
  if (cartDrawer) {
    const dialog = cartDrawer.querySelector('dialog');
    if (dialog) {
      observer.observe(dialog, { childList: true, subtree: true });
      dialog.addEventListener('open', () => scheduleSync());
    }
  }

  scheduleSync();
});
